import React, { useEffect, useState } from 'react';

// Inject the keyframes once. Using a <style> tag instead of inline styles
// because CSS animations can't be expressed inline.
const STYLE_ID = 'teamsly-top-loading-bar-keyframes';
const ensureStyles = () => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
@keyframes teamsly-tlb-grow {
    0%   { width: 0%;   opacity: 1; }
    25%  { width: 35%;  opacity: 1; }
    60%  { width: 65%;  opacity: 1; }
    100% { width: 85%;  opacity: 1; }
}
@keyframes teamsly-tlb-finish {
    0%   { opacity: 1; }
    40%  { opacity: 1; }
    100% { opacity: 0; }
}
`;
    document.head.appendChild(style);
};

/**
 * Thin top-of-page progress bar (NProgress / Azure DevOps style).
 *
 * Sits *below* the nav (default offset: 48px — matches the rendered
 * `.navigation-navbar` height) with a subtle, downward-only shadow so the
 * glow never bleeds up over the nav.
 *
 * Props:
 *   active    — boolean: true while the next route's chunk is loading.
 *   topOffset — px offset from the top of the viewport. Default 48.
 */
const TopLoadingBar = ({ active, topOffset = 48 }) => {
    // 0 → hidden, 1 → loading (animated grow), 2 → finishing (snap + fade)
    const [stage, setStage] = useState(0);

    useEffect(() => { ensureStyles(); }, []);

    useEffect(() => {
        if (active) {
            setStage(1);
            return;
        }
        let hideTimer;
        setStage((prev) => {
            if (prev === 1) {
                hideTimer = setTimeout(() => setStage(0), 450);
                return 2;
            }
            return 0;
        });
        return () => { if (hideTimer) clearTimeout(hideTimer); };
    }, [active]);

    if (stage === 0) return null;

    const finishing = stage === 2;

    return (
        <div
            aria-hidden="true"
            style={{
                position: 'fixed',
                top: topOffset,
                left: 0,
                right: 0,
                height: 3,
                // High enough to sit above page content (.report-filter-bar
                // and others use z-index: 2 with position: relative). The bar
                // is anchored at `top: topOffset` (below the nav) so a high
                // z-index does not cause it to appear "over" the nav.
                zIndex: 1000,
                pointerEvents: 'none',
                overflow: 'hidden',
            }}
        >
            <div
                key={finishing ? 'finish' : 'load'}
                style={{
                    height: '100%',
                    width: finishing ? '100%' : '0%',
                    background:
                        'linear-gradient(90deg, rgba(0,0,0,0) 0%, var(--themePrimary2, #0078d4) 25%, var(--themePrimary2, #0078d4) 100%)',
                    // Downward-only shadow — y-offset positive, negative spread
                    // confines the blur to below the bar so it never spills up
                    // onto the nav above.
                    boxShadow: '0 2px 4px -1px rgba(0, 120, 212, 0.35)',
                    willChange: 'width, opacity',
                    animation: finishing
                        ? 'teamsly-tlb-finish 400ms ease-out forwards'
                        : 'teamsly-tlb-grow 10s cubic-bezier(0, 0.6, 0.4, 1) forwards',
                    // For the "finish" stage we transition width quickly to 100%
                    // alongside the fade-out animation.
                    transition: finishing ? 'width 250ms ease-out' : undefined,
                }}
            />
        </div>
    );
};

export default TopLoadingBar;

